import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Activity,
  Calendar,
  User,
  FileText,
  Search,
  LogOut,
  Clock,
  Video,
  Home,
  Building,
  ChevronRight,
  Bell,
  Settings,
  Menu,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Edit,
  Save,
  Phone,
  Mail,
  MapPin,
  Heart,
  Star,
  CalendarClock,
  RefreshCw,
  Eye,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiRequest, formatDate, getInitials, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';
import { PatientAppointmentCard } from '../components/PatientAppointmentCard';

const PHYSIGN_LOGO = "https://customer-assets.emergentagent.com/job_4bf5f59f-ff2a-4b89-ab5f-09ffb3b85c36/artifacts/91dqnx5f_high-resolution-color-logo-3.png";

export default function PatientDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, logout, isAuthenticated, loading: authLoading, updateProfile, checkAuth } = useAuth();
  
  const [appointments, setAppointments] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('appointments');
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Reschedule
  const [rescheduleModal, setRescheduleModal] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  
  // Review
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [reviewedApts, setReviewedApts] = useState(new Set());
  
  // Cancel confirmation
  const [cancelConfirm, setCancelConfirm] = useState(null);
  
  // View appointment detail
  const [viewApt, setViewApt] = useState(null);
  
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    date_of_birth: '',
    gender: '',
    blood_group: '',
    address: '',
    emergency_contact: '',
    allergies: '',
    medical_history: '',
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'patient') {
      fetchData();
      // Populate profile form
      setProfileData({
        name: user?.name || '',
        email: user?.email || '',
        date_of_birth: profile?.date_of_birth || '',
        gender: profile?.gender || '',
        blood_group: profile?.blood_group || '',
        address: profile?.address || '',
        emergency_contact: profile?.emergency_contact || '',
        allergies: profile?.allergies?.join(', ') || '',
        medical_history: profile?.medical_history?.join(', ') || '',
      });
    }
  }, [isAuthenticated, user, profile]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [aptsData, recordsData, notifsData, myReviewsData] = await Promise.all([
        apiRequest('/appointments'),
        apiRequest('/medical-records'),
        apiRequest('/notifications'),
        apiRequest('/reviews/my').catch(() => ({ reviews: [] })),
      ]);
      setAppointments(aptsData.appointments || []);
      setRecords(recordsData.records || []);
      setNotifications(notifsData.notifications || []);
      setUnreadCount(notifsData.unread_count || 0);
      // Mark already-reviewed appointments so UI shows "Reviewed" instead of "Leave Review" after page refresh
      const reviewedSet = new Set((myReviewsData.reviews || []).map(r => r.appointment_id).filter(Boolean));
      setReviewedApts(reviewedSet);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    try {
      await apiRequest(`/appointments/${appointmentId}/status?status=cancelled`, {
        method: 'PUT',
      });
      toast.success('Appointment cancelled');
      setCancelConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) {
      toast.error('Please select date and time');
      return;
    }
    try {
      await apiRequest(`/appointments/${rescheduleModal}/reschedule`, {
        method: 'PUT',
        body: JSON.stringify({ appointment_date: rescheduleDate, time_slot: rescheduleTime }),
      });
      toast.success('Appointment rescheduled');
      setRescheduleModal(null);
      setRescheduleDate('');
      setRescheduleTime('');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewModal) return;
    try {
      await apiRequest('/reviews', {
        method: 'POST',
        body: JSON.stringify({
          appointment_id: reviewModal.id,
          provider_id: reviewModal.provider_id,
          rating: reviewRating,
          review_text: reviewText,
        }),
      });
      toast.success('Review submitted!');
      setReviewedApts(prev => new Set([...prev, reviewModal.id]));
      setReviewModal(null);
      setReviewRating(5);
      setReviewText('');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await apiRequest('/notifications/read-all', { method: 'PUT' });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const updateData = {
        name: profileData.name,
        email: profileData.email,
        date_of_birth: profileData.date_of_birth,
        gender: profileData.gender,
        blood_group: profileData.blood_group,
        address: profileData.address,
        emergency_contact: profileData.emergency_contact,
        allergies: profileData.allergies ? profileData.allergies.split(',').map(s => s.trim()).filter(Boolean) : [],
        medical_history: profileData.medical_history ? profileData.medical_history.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      
      await apiRequest('/patient/profile', {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      
      toast.success('Profile updated successfully!');
      await checkAuth(); // Refresh user data
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getServiceIcon = (service) => {
    const icons = {
      online: Video,
      home_visit: Home,
      clinic: Building,
    };
    const Icon = icons[service] || Building;
    return <Icon className="w-4 h-4" />;
  };

  const upcomingAppointments = appointments.filter(a => ['pending', 'confirmed'].includes(a.status));
  const pastAppointments = appointments.filter(a => ['completed', 'cancelled', 'rejected'].includes(a.status));

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F62FE]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="patient-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <Link to="/" className="flex items-center gap-2">
                <span className="hidden sm:block text-xl font-bold text-[#3B1C73]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  Physign
                </span>
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative"
                  onClick={() => setShowNotifications(!showNotifications)}
                  data-testid="notification-bell"
                >
                  <Bell className="w-5 h-5 text-slate-600" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 rounded-full text-white text-[10px] flex items-center justify-center font-bold px-1" data-testid="unread-count">
                      {unreadCount}
                    </span>
                  )}
                </Button>

                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 top-12 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 max-h-96 overflow-hidden" data-testid="notifications-dropdown">
                    <div className="p-3 border-b flex items-center justify-between">
                      <h3 className="font-semibold text-sm">Notifications</h3>
                      {unreadCount > 0 && (
                        <button className="text-xs text-blue-600 hover:underline" onClick={handleMarkAllRead} data-testid="mark-all-read">
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="overflow-y-auto max-h-72">
                      {notifications.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-6">No notifications</p>
                      ) : (
                        notifications.map(n => (
                          <div key={n.id} className={`p-3 border-b border-slate-50 text-sm ${n.is_read ? 'bg-white' : 'bg-blue-50'}`}>
                            <p className="font-medium text-slate-800">{n.title}</p>
                            <p className="text-slate-500 text-xs mt-0.5">{n.message}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#E0F2FE] flex items-center justify-center text-sm font-medium text-[#0F62FE]">
                      {getInitials(user?.name)}
                    </div>
                    <span className="hidden sm:block text-sm font-medium">
                      {user?.name || 'Patient'}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => {
                    setActiveTab('profile');
                    document.getElementById('my-appointments-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}>
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    setActiveTab('profile');
                    document.getElementById('my-appointments-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 py-4">
            <div className="px-4 space-y-2">
              <Link 
                to="/search" 
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Search className="w-5 h-5 text-slate-600" />
                <span>Find Doctors</span>
              </Link>
              <Link 
                to="/dashboard" 
                className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 text-[#0F62FE]"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Calendar className="w-5 h-5" />
                <span>Appointments</span>
              </Link>
              <Link 
                to="/records" 
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                <FileText className="w-5 h-5 text-slate-600" />
                <span>Medical Records</span>
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Welcome back, {user?.name?.split(' ')[0] || 'there'}!
          </h1>
          <p className="text-slate-600 mt-1">Manage your appointments and health records</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/search?role=doctor')}
            data-testid="find-doctor-card"
          >
            <CardContent className="p-4 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-3">
                <Search className="w-6 h-6 text-[#0F62FE]" />
              </div>
              <p className="font-medium text-slate-900">Find Doctor</p>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/search?role=nurse')}
            data-testid="find-nurse-card"
          >
            <CardContent className="p-4 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-3">
                <Home className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="font-medium text-slate-900">Home Visit</p>
            </CardContent>
          </Card>
          {/*
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/search?role=lab')}
            data-testid="lab-test-card"
          >
            <CardContent className="p-4 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-3">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <p className="font-medium text-slate-900">Lab Tests</p>
            </CardContent>
          </Card>
          */}
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/search?role=nurse')}
            data-testid="Find-nurse-card"
          >
            <CardContent className="p-4 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-3">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <p className="font-medium text-slate-900">Find Nurse</p>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => {
              setActiveTab('appointments');
              document.getElementById('my-appointments-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            data-testid="appointments-card"
          >
            <CardContent className="p-4 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mb-3">
                <Calendar className="w-6 h-6 text-amber-600" />
              </div>
              <p className="font-medium text-slate-900">Appointments</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs Navigation */}
        <Tabs id="my-appointments-section" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-2 w-full max-w-md">
            <TabsTrigger value="appointments" data-testid="appointments-tab">
              <Calendar className="w-4 h-4 mr-2" />
              Appointments
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="profile-tab">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            {/* Records tab - coming soon
            <TabsTrigger value="records" data-testid="records-tab">
              <FileText className="w-4 h-4 mr-2" />
              Records
            </TabsTrigger>
            */}
          </TabsList>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>My Appointments</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/search')}
                  >
                    Book New
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="upcoming">
                  <TabsList className="mb-4">
                    <TabsTrigger value="upcoming">
                      Upcoming ({upcomingAppointments.length})
                    </TabsTrigger>
                    <TabsTrigger value="past">
                      Past ({pastAppointments.length})
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="upcoming" className="space-y-4">
                    {upcomingAppointments.length === 0 ? (
                      <div className="text-center py-8">
                        <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-600 mb-4">No upcoming appointments</p>
                        <Button onClick={() => navigate('/search')} className="bg-[#0F62FE]">
                          Book Appointment
                        </Button>
                      </div>
                    ) : (
                      upcomingAppointments.map((apt) => (
                        <PatientAppointmentCard
                          key={apt.id}
                          apt={apt}
                          onView={setViewApt}
                          onReschedule={(a) => {
                            setRescheduleModal(a.id);
                            setRescheduleDate(a.appointment_date);
                            setRescheduleTime(a.time_slot);
                          }}
                          onCancel={(id) => setCancelConfirm(id)}
                          cancelConfirmId={cancelConfirm}
                          onCancelConfirm={handleCancelAppointment}
                          onCancelDismiss={() => setCancelConfirm(null)}
                        />
                      ))
                    )}
                  </TabsContent>

                  <TabsContent value="past" className="space-y-4">
                    {pastAppointments.length === 0 ? (
                      <div className="text-center py-8">
                        <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-600">No past appointments</p>
                      </div>
                    ) : (
                      pastAppointments.map((apt) => (
                        <PatientAppointmentCard
                          key={apt.id}
                          apt={apt}
                          onView={setViewApt}
                          onReview={(a) => setReviewModal(a)}
                          isReviewed={reviewedApts.has(apt.id)}
                        />
                      ))
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  My Profile
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Basic Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Basic Information
                    </h3>
                    
                    <div>
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        placeholder="Enter your full name"
                        value={profileData.name}
                        onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={profileData.email}
                        onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        value={user?.phone || ''}
                        disabled
                        className="mt-1 bg-slate-50"
                      />
                      <p className="text-xs text-slate-500 mt-1">Phone number cannot be changed</p>
                    </div>
                    
                    <div>
                      <Label htmlFor="dob">Date of Birth</Label>
                      <Input
                        id="dob"
                        type="date"
                        value={profileData.date_of_birth}
                        onChange={(e) => setProfileData({...profileData, date_of_birth: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="gender">Gender</Label>
                      <Select 
                        value={profileData.gender} 
                        onValueChange={(value) => setProfileData({...profileData, gender: value})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Health Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Heart className="w-4 h-4" />
                      Health Information
                    </h3>
                    
                    <div>
                      <Label htmlFor="blood">Blood Group</Label>
                      <Select 
                        value={profileData.blood_group} 
                        onValueChange={(value) => setProfileData({...profileData, blood_group: value})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select blood group" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="A+">A+</SelectItem>
                          <SelectItem value="A-">A-</SelectItem>
                          <SelectItem value="B+">B+</SelectItem>
                          <SelectItem value="B-">B-</SelectItem>
                          <SelectItem value="AB+">AB+</SelectItem>
                          <SelectItem value="AB-">AB-</SelectItem>
                          <SelectItem value="O+">O+</SelectItem>
                          <SelectItem value="O-">O-</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="allergies">Allergies</Label>
                      <Textarea
                        id="allergies"
                        placeholder="Enter allergies separated by commas (e.g., Peanuts, Penicillin)"
                        value={profileData.allergies}
                        onChange={(e) => setProfileData({...profileData, allergies: e.target.value})}
                        className="mt-1"
                        rows={2}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="history">Medical History</Label>
                      <Textarea
                        id="history"
                        placeholder="Enter medical conditions separated by commas (e.g., Diabetes, Hypertension)"
                        value={profileData.medical_history}
                        onChange={(e) => setProfileData({...profileData, medical_history: e.target.value})}
                        className="mt-1"
                        rows={2}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="emergency">Emergency Contact</Label>
                      <Input
                        id="emergency"
                        placeholder="Emergency contact number"
                        value={profileData.emergency_contact}
                        onChange={(e) => setProfileData({...profileData, emergency_contact: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="address">Address</Label>
                      <Textarea
                        id="address"
                        placeholder="Enter your full address"
                        value={profileData.address}
                        onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                        className="mt-1"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 flex justify-end">
                  <Button 
                    className="bg-[#0F62FE] hover:bg-[#0050E6]"
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    data-testid="save-profile-btn"
                  >
                    {savingProfile ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Profile
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Medical Records Tab - Coming Soon
          <TabsContent value="records">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Medical Records
                </CardTitle>
              </CardHeader>
              <CardContent>
                {records.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No medical records yet</h3>
                    <p className="text-slate-600">Your prescriptions and reports will appear here after appointments</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {records.map((record, index) => (
                      <div key={record.id} className="flex items-center gap-4 p-4 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors">
                        <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                          <FileText className="w-6 h-6 text-[#0F62FE]" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{record.title}</p>
                          <p className="text-sm text-slate-500 capitalize">{record.record_type}</p>
                          {record.description && (
                            <p className="text-sm text-slate-600 mt-1">{record.description}</p>
                          )}
                        </div>
                        <p className="text-sm text-slate-400">{formatDate(record.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          */}
        </Tabs>
      </main>

      {/* Reschedule Modal */}
      {rescheduleModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="reschedule-modal">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-[#0F62FE]" />
                Reschedule Appointment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>New Date</Label>
                <Input
                  type="date"
                  value={rescheduleDate}
                  onChange={e => setRescheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="mt-1"
                  data-testid="reschedule-date"
                />
              </div>
              <div>
                <Label>New Time Slot</Label>
                <Select value={rescheduleTime} onValueChange={setRescheduleTime}>
                  <SelectTrigger className="mt-1" data-testid="reschedule-time">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {['09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00'].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setRescheduleModal(null)}>Cancel</Button>
                <Button className="bg-[#0F62FE]" onClick={handleReschedule} data-testid="confirm-reschedule-btn">
                  <RefreshCw className="w-4 h-4 mr-1" /> Confirm Reschedule
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="review-modal" onClick={() => setReviewModal(null)}>
          <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                Rate Your Experience
              </CardTitle>
              <p className="text-sm text-slate-500">
                {reviewModal.provider?.user?.name || 'Provider'} · {formatDate(reviewModal.appointment_date)}
              </p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label>How was your experience?</Label>
                <div className="flex gap-2 mt-3" data-testid="star-rating">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => setReviewRating(star)}
                      className="focus:outline-none hover:scale-110 transition-transform"
                      data-testid={`star-${star}`}
                      aria-label={`${star} star${star > 1 ? 's' : ''}`}
                    >
                      <Star
                        className={`w-10 h-10 transition-colors ${star <= reviewRating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
                      />
                    </button>
                  ))}
                </div>
                <p className="text-sm font-medium text-slate-700 mt-2" data-testid="rating-label">
                  {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][reviewRating]}
                </p>
              </div>
              <div>
                <Label>Tell us more (optional)</Label>
                <Textarea
                  value={reviewText}
                  onChange={e => setReviewText(e.target.value)}
                  placeholder="What did you like? Anything to improve?"
                  className="mt-1.5"
                  rows={4}
                  maxLength={500}
                  data-testid="review-text"
                />
                <p className="text-xs text-slate-400 mt-1 text-right">{reviewText.length}/500</p>
              </div>
              <div className="flex gap-2 justify-end pt-2 border-t border-slate-100">
                <Button variant="outline" onClick={() => setReviewModal(null)} data-testid="cancel-review-btn">Cancel</Button>
                <Button
                  className="text-white"
                  style={{ background: '#3B1C73' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#2c1457')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '#3B1C73')}
                  onClick={handleSubmitReview}
                  data-testid="submit-review-btn"
                >
                  Submit Review
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View Appointment Detail Modal */}
      {viewApt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="view-appointment-modal" onClick={() => setViewApt(null)}>
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Eye className="w-5 h-5 text-[#0F62FE]" />
                  Appointment Details
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setViewApt(null)} className="h-8 w-8">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Badge className="bg-slate-100 text-slate-700 font-mono text-xs" data-testid="view-modal-booking-id">
                  {(() => {
                    const prefix = viewApt.service_type === 'home_visit' ? 'HV' : viewApt.service_type === 'online' ? 'ON' : 'CL';
                    const num = (viewApt.id || '').replace(/\D/g, '').slice(0, 5).padStart(5, '0');
                    return `PHY-${prefix}-${num}`;
                  })()}
                </Badge>
                <Badge className={getStatusColor(viewApt.status)} data-testid="view-modal-status">{viewApt.status}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Provider Info */}
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-[#E0F2FE] flex items-center justify-center font-bold text-[#0F62FE]">
                  {getInitials(viewApt.provider?.user?.name || 'Dr')}
                </div>
                <div>
                  <p className="font-semibold text-slate-900" data-testid="view-modal-provider-name">{viewApt.provider?.user?.name || 'Healthcare Provider'}</p>
                  <p className="text-sm text-slate-600 capitalize">{viewApt.provider?.profile?.specialization || viewApt.provider_type || ''}</p>
                  {viewApt.provider?.user?.phone && (
                    <a href={`tel:${viewApt.provider.user.phone}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5">
                      <Phone className="w-3 h-3" />{viewApt.provider.user.phone}
                    </a>
                  )}
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400 mb-1">Date</p>
                  <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-slate-400" />{formatDate(viewApt.appointment_date)}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400 mb-1">Time</p>
                  <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-slate-400" />{viewApt.time_slot}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400 mb-1">Type</p>
                  <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                    {viewApt.service_type === 'home_visit' ? <Home className="w-4 h-4 text-slate-400" /> : viewApt.service_type === 'online' ? <Video className="w-4 h-4 text-slate-400" /> : <Building className="w-4 h-4 text-slate-400" />}
                    {viewApt.service_type === 'home_visit' ? 'Home Visit' : viewApt.service_type === 'online' ? 'Online' : 'Clinic Visit'}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400 mb-1">Fee</p>
                  <p className="text-sm font-medium text-slate-800">₹{viewApt.fee || 500}</p>
                </div>
              </div>

              {viewApt.address && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400 mb-1">Address</p>
                  <p className="text-sm font-medium text-slate-800 flex items-start gap-1.5">
                    <MapPin className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />{viewApt.address}
                  </p>
                </div>
              )}

              {viewApt.symptoms && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400 mb-1">Reason for Visit</p>
                  <p className="text-sm font-medium text-slate-800">{viewApt.symptoms}</p>
                </div>
              )}

              {viewApt.notes && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400 mb-1">Notes</p>
                  <p className="text-sm text-slate-700">{viewApt.notes}</p>
                </div>
              )}

              {viewApt.prescription && (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-xs text-emerald-600 mb-1 font-medium">Prescription</p>
                  <p className="text-sm text-slate-800">{viewApt.prescription}</p>
                </div>
              )}

              <div className="pt-2 flex justify-end">
                <Button variant="outline" onClick={() => setViewApt(null)}>Close</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
